import { type ReactNode, type FC } from "react"
import styled from "styled-components"
import Values from "values.js"

import LexicalViewer from "@forento/shared-client/components/LexicalViewer"

import { lightTextColor } from "~/utilities/styles"

type Props = {
	title: string
	thumbnailUrl: string | null
	htmlDescription: string | null
	tags?: { id: number; label: string }[]
	callToAction: ReactNode
}
const Hero: FC<Props> = ({ title, thumbnailUrl, htmlDescription, tags, callToAction }) => (
	<Container>
		<Content>
			<Details>
				<Title>{title}</Title>
				{htmlDescription && <LexicalViewer html={htmlDescription} className="text-light" />}
				{tags && tags.length > 0 && (
					<div className="flex flex-wrap gap-2">
						{tags.map(tag => (
							<div
								key={tag.id}
								className="inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary"
							>
								{tag.label}
							</div>
						))}
					</div>
				)}
				{callToAction}
			</Details>
			<ThumbnailContainer $hasThumbnail={Boolean(thumbnailUrl)}>
				{thumbnailUrl ? <Thumbnail alt="Product thumbnail" src={thumbnailUrl} /> : <div />}
			</ThumbnailContainer>
		</Content>
	</Container>
)

export const CallToActionIcon = styled.div`
	width: 16px;
	height: 16px;
`

export const CallToActionInfo = styled.p`
	font-size: 16px;
	color: ${lightTextColor};
	font-style: italic;
`

export const CallToActionButtons = styled.div`
	display: flex;
	gap: 16px;
`

const Container = styled.div`
	@media (max-width: 999px) {
		margin: 0 16px;
	}
`

const Content = styled.div`
	background-color: ${props => new Values(props.theme.primaryColor).tint(90).hexString()};
	border-radius: 16px;
	display: flex;
	padding: 42px;
	margin-bottom: 42px;

	@media (max-width: 999px) {
		flex-direction: column;
		gap: 32px;
	}
	@media (min-width: 1000px) {
		gap: ${42 * 2}px;
	}
`

const Details = styled.div`
	flex: 1 0 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 22px;
`

const Title = styled.h1`
	font-weight: 500;
	font-size: 24px;
	color: #151d48;
`

const ThumbnailContainer = styled.div<{ $hasThumbnail: boolean }>`
	border-radius: 8px;

	@media (max-width: 999px) {
		width: 100%;
		${props => props.$hasThumbnail && "aspect-ratio: 16 / 12;"}
	}
	@media (min-width: 1000px) {
		flex: 1 0 0;
		aspect-ratio: 16 / 12;
	}
`

const Thumbnail = styled.img`
	object-fit: cover;
`

export default Hero
